<script setup lang="ts">
import { ref, computed, watch, inject, onMounted } from 'vue';

import useWebsiteTexts from '@/composables/useWebsiteTexts';
import { configuratorStateKey } from '../ProductConfiguratorKeys';

const { websiteText } = useWebsiteTexts();
const emit = defineEmits(['selected', 'desiredWidthUpdated', 'desiredHeightUpdated']);
const { configuratorState } = inject(configuratorStateKey);

const props = defineProps({
  productVariants: {
    type: Array,
    required: true,
  },
  variants: {
    type: Array,
    required: true,
  },
  selected: {
    type: String,
    default: '',
  },
  colors: {
    type: Object,
  },
  desiredWidth: {
    type: Number,
    default: 0,
  },
  desiredHeight: {
    type: Number,
    default: 0,
  },
});

const inputColors = ref(props.colors || {});

const desiredWidthModel = ref(props.desiredWidth ? props.desiredWidth.toString() : '');
const desiredWidth = ref(0);

const desiredHeightModel = ref(props.desiredHeight ? props.desiredHeight.toString() : '');
const desiredHeight = ref(0);

const titleDisplayText = computed(() => {
  const v = props.variants[0];
  if (!v?.nrOfColors && !v?.printAreaFromCM2 && !v?.printAreaToCM2) return '';

  return v.printAreaToCM2
    ? websiteText('decoration__variant_title_size').value
    : websiteText('decoration__variant_title_color').value;
});

function variantColorType(v) {
  return v.nrOfColors === 1
    ? websiteText('decoration__variant_color_single',
      { amount: v.nrOfColors }).value
    : websiteText('decoration__variant_color_multiple',
      { amount: v.nrOfColors }).value;
}

const sortedVariants = computed(() => {
  return Array.from(props.variants).sort((a, b) => a.nrOfColors - b.nrOfColors);
});

function variantDisplayTexts(variant) {
  if (!variant) return '';
  return variant.printAreaToCM2
    ? `${variant.printAreaFromCM2} - ${variant.printAreaToCM2}cm²`
    : (variant.nrOfColors 
      ? variantColorType(variant) 
      : variant.name);
}

const selectedVariant = ref([...props.variants]
  .find(v => v.systemId === props.selected) || {});

const numberOfColorVariants = computed(() => {
  return (!selectedVariant.value.printAreaToCM2 && selectedVariant.value.nrOfColors) || 0;
});

const limitedColorsSelected = computed(() => {
  let colors = {};

  if (numberOfColorVariants.value && (Object.keys(inputColors.value).length > numberOfColorVariants.value)) {
    for (let index = 1; index <= numberOfColorVariants.value; index++) {
      colors[`color${index}`] = inputColors.value[`color${index}`];
    }
  } else {
    colors = inputColors.value;
  }

  return colors;
});

function selectVariant(variant: object) {
  selectedVariant.value = variant;
  emit('selected', variant.systemId, limitedColorsSelected.value);
}

const hasSizeBasedVariants = computed(() => {
  return props.variants.filter(v => v.printAreaToCM2).length != 0;
});

function saveColorInput(val, index: string) {
  inputColors.value[`color${index}`] = val;
  emit('selected', selectedVariant.value.systemId, limitedColorsSelected.value);
}

function selectVariantBasedOnArea() {
  desiredWidth.value = parseInt(desiredWidthModel.value, 10);
  if (Number.isNaN(desiredWidth.value)) {
    return;
  }

  desiredHeight.value = parseInt(desiredHeightModel.value, 10);
  if (Number.isNaN(desiredHeight.value)) {
    return;
  }

  const areaInCm2 = (desiredWidth.value * desiredHeight.value) / 100;

  const matchingVariants = props.variants.filter(v => v.printAreaFromCM2 <= areaInCm2 && v.printAreaToCM2 >= areaInCm2);
  if (matchingVariants.length !== 0) {
    selectVariant(matchingVariants[0]);
  }
  else {
    const largestVariants = props.variants
            .filter(v => v.printAreaFromCM2 <= areaInCm2)
            .sort((a, b) => (a.printAreaFromCM2 < b.printAreaFromCM2) ? 1 : -1);
    if (largestVariants.length !== 0) {
      selectVariant(largestVariants[0]);
    }
  }
}

function validateWidth() {
  if(desiredWidthModel.value) {
    desiredWidthModel.value = desiredWidthModel.value.replace(/\D/g, '');
  }
}

function validateHeight() {
  if(desiredHeightModel.value) {
    desiredHeightModel.value = desiredHeightModel.value.replace(/\D/g, '');
  }
}

watch(desiredWidthModel, () => selectVariantBasedOnArea());
watch(desiredHeightModel, () => selectVariantBasedOnArea());

watch(desiredWidth, () => emit('desiredWidthUpdated', desiredWidth.value));
watch(desiredHeight, () => emit('desiredHeightUpdated', desiredHeight.value));

const notAvailableText = computed(() => {
  if (!selectedVariant.value) {
    return '';
  }

  const selectedTechniqueVariant = sortedVariants.value.find(v=>v.systemId == selectedVariant.value.systemId);
  let notAvailableVariants = [];

  for (var i=0; i < props.productVariants.length; i++) {
    const prodVariant = props.productVariants[i];
    if (selectedTechniqueVariant.supportedVariants.filter(v=> v == prodVariant.systemId).length == 0) {
      notAvailableVariants.push(prodVariant.fields?.Color?.[0]?.name || '');
    }
  }

  if (notAvailableVariants.length == 0) {
    return '';
  }

  const notAvailableVariantsText = notAvailableVariants.join(', ');
  return websiteText('decoration__not_available', {
    decoration: selectedTechniqueVariant.name,
    variant: notAvailableVariantsText
  }).value;

});

onMounted(() => {
  desiredWidthModel.value = props.desiredWidth ? props.desiredWidth.toString() : '';
  desiredHeightModel.value = props.desiredHeight ? props.desiredHeight.toString() : '';
})
</script>

<template>
  <div class="decoration-technique-variants">
    <div class="decoration-technique-variants__side-gutter">
      <img src="../../../static/icons/turn-arrow.svg" alt="">
    </div>
    <div class="decoration-technique-variants__main">
      <p class="decoration-technique-variants__title">
        {{titleDisplayText}}
      </p>
      <div class="decoration-technique-variants__variant-button-container">
        <button
          :class="['decoration-technique-variants__variant-button', {
            'decoration-technique-variants__variant-button--selected'
            : variant.systemId === selectedVariant.systemId}]"
          v-for="variant in sortedVariants"
          :key="variant.systemId"
          @click="selectVariant(variant)"
        >
          <img
            :class="['decoration-technique-variants__variant-checkmark', {
            'decoration-technique-variants__variant-checkmark--selected'
            : variant.systemId === selectedVariant.systemId}]"
            src="@/static/icons/checkmark_white_icon.svg"
            alt="white checkmark"
          >
          {{variantDisplayTexts(variant)}}
        </button>
      </div>
      <div
        v-if="hasSizeBasedVariants"
      >
        <p class="decoration-technique-variants__title">
          {{ websiteText('decoration__input_size').value }}
        </p>
        <div class="decoration-technique-variants__input-container">
          <label class="decoration-technique-variants__label">{{ websiteText('decoration__width').value }}</label>
          <input
            v-model="desiredWidthModel"
            @keyup="validateWidth()"
            class="decoration-technique-variants__input"
            type="text"
          >
        </div>
        <div class="decoration-technique-variants__input-container">
          <label class="decoration-technique-variants__label">{{ websiteText('decoration__height').value }}</label>
          <input
            v-model="desiredHeightModel"
            @keyup="validateHeight()"
            class="decoration-technique-variants__input"
            type="text"
          >
        </div>
      </div>

      <div
        class="decoration-technique-variants--color-title"
        v-if="numberOfColorVariants"
      >
        {{ websiteText('decoration__variant_color_title').value }}
      </div>
      <div
        class="decoration-technique-variants--color-subtitle"
        v-if="numberOfColorVariants"
      >
        {{ websiteText('decoration__variant_color_subtitle').value }}
      </div>
      <div
        v-for="number in numberOfColorVariants"
        :key="number"
        class="decoration-technique-variants__color-input-container"
      >
        <label class="decoration-technique-variants__color-input-text"
          :for="`color-input-${number}`"
        >
          {{ websiteText('decoration__variant_color').value }}&nbsp;{{ number }}:
        </label>
        <input
          class="decoration-technique-variants__color-input-field"
          :id="`color-input-${number}`"
          type="text"
          maxlength="30"
          v-model="inputColors[`color${number}`]"
          @change="saveColorInput(inputColors[`color${number}`], number)"
        >
      </div>
    </div>
    <div class="decoration-technique-variants__warning" v-if="notAvailableText">
      <img
          class="decoration-technique-variants__warning_icon"
          src="../../../static/icons/info.svg"
        />
      {{ notAvailableText }}
    </div>
  </div>
</template>

<style>
  .decoration-technique-variants {
    display: flex;
    margin-top: 10px;
  }

  .decoration-technique-variants__side-gutter {
    min-width: 30px;
    border-right: 1px solid var(--color-neutrals-20);
    margin-right: 15px;
  }

  .decoration-technique-variants__main {
    margin-top: 5px;
  }

  .decoration-technique-variants__title {
    margin: 0;
    font-size: 14px;
    line-height: 16px;
    color: var(--color-text-darker);
    user-select: none;
    margin-bottom: 10px;
    font-style: italic;
  }

  .decoration-technique-variants__variant-button-container {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -5px;
  }

  .decoration-technique-variants__variant-button {
    border-radius: 100px;
    border: 1px solid var(--color-neutrals-50);
    margin: 0 5px 10px;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 16px;
  }

  .decoration-technique-variants__variant-button--selected {
    background-color: var(--color-neutrals-100);
    color: var(--color-white);
    border: 1px solid var(--color-neutrals-100);
  }

  .decoration-technique-variants__variant-checkmark {
    margin-right: 0;
    width: 0;
    opacity: 0;
    transition: all 0.1s ease-in;
  }

  .decoration-technique-variants__variant-checkmark--selected {
    margin-right: 4px;
    opacity: 1;
    width: 15px;
  }

  .decoration-technique-variants__input-container {
    display: flex;
    flex-flow: row;
    justify-content: flex-start;
    padding: 4px;
    align-items: center;
  }

  .decoration-technique-variants__input[type='text'] {
    max-width: 84px;
    height: 30px;
    -moz-appearance: textfield;
  }

  .decoration-technique-variants__label {
    min-width: 84px;
  }

  .decoration-technique-variants--color-title {
    margin-top: 25px;
    line-height: 24px;
    font-size: 14px;
  }

  .decoration-technique-variants--color-subtitle {
    color: var(--color-neutrals-80);
    line-height: 24px;
    font-size: 14px;
  }

  .decoration-technique-variants__color-input-container {
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    margin-top: 10px;
  }

  .decoration-technique-variants__color-input-text {
    font-size: 14px;
    font-style: italic;
    margin-right: 5px;
  }

  .decoration-technique-variants__warning {
    display: flex;
    align-items: flex-start;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #5379FF;
  }

  .decoration-technique-variants__warning_icon {
    width: 20px;
    height: 20px;
    padding-right: 4px;
    margin-top: 3px;
  }
</style>
